// src/utils/trialHelper.js
// Pure trial-tracking logic — status labels, the trial status machine, and
// funnel math. No Firestore calls, no React. Kept separate from
// AdminDashboard's computeStatsForRange so a bug in one can't corrupt the other.

import { resolveTimezone } from '../config/locationTimezones';

// ─── Status constants ──────────────────────────────────────────────────────

export const TRIAL_STATUSES = [
  'requested',
  'scheduled',
  'held',
  'no_show',
  'cancelled',
  'won',
  'lost',
  'expired',
];

export const TRIAL_STATUS_LABELS = {
  requested: 'Requested',
  scheduled: 'Scheduled',
  held:      'Held',
  no_show:   'No-Show',
  cancelled: 'Cancelled',
  won:       'Won',
  lost:      'Lost',
  expired:   'Expired',
};

export const DEFAULT_TRIAL_DURATION_MINS = 30;

// Values match teachers.dialect exactly (TeacherManager.js DIALECTS) so a lead's
// stated preference can be matched to a teacher later. Matching is on value, so
// labels are free to differ — 'palestinian' is spelled correctly here even
// though the teacher list's label has a pre-existing "Palastinian" typo (fixed
// there separately, value unchanged). 'fusha' keeps the teacher wording ("Fusha
// (MSA)", not "Modern Standard Arabic"). 'other' records a request we can't
// currently staff — it's not an offer. 'no_preference' is intake-only and
// intentionally matches no teacher record; kept last. Khaleeji and Moroccan are
// deliberately excluded — we don't staff them.
export const TRIAL_DIALECTS = [
  { value: 'egyptian',      label: 'Egyptian' },
  { value: 'lebanese',      label: 'Lebanese' },
  { value: 'palestinian',   label: 'Palestinian' },
  { value: 'syrian',        label: 'Syrian' },
  { value: 'fusha',         label: 'Fusha (MSA)' },
  { value: 'other',         label: 'Other' },
  { value: 'no_preference', label: 'No preference' },
];

// "How did you hear about us?" — value === label. Shared by the admin trial form
// (TrialManager) and the public trial form (TrialSignup); the createTrialSignup
// Cloud Function keeps its own mirror (TRIAL_HEARD_ABOUT_VALUES) and validates
// against it, so keep all three in sync.
export const HEARD_ABOUT_OPTIONS = [
  'Instagram',
  'Facebook',
  'Google Search',
  'Friend or Family',
  'Referral Code',
  'Other',
];

// ─── Status machine ─────────────────────────────────────────────────────────
// 'expired' is reached manually in Phase 0 (no scheduled job yet), but it's
// still a legal transition from 'requested' — not an override.
//
// scheduled/no_show/cancelled → scheduled are reschedules: moving an upcoming
// trial, giving a no-show another slot, or bringing back a cancelled one.
// held → scheduled is deliberately NOT legal — a held trial wanting another
// session is a different thing we're not modelling in Phase 0.
//
// PHASE 2 — the `won` edges from requested / scheduled / cancelled / lost /
// expired are PAYMENT-DRIVEN, not ordinary admin transitions. A completed
// trial_conversion checkout, or an admin recording an off-platform payment
// (e-transfer, phone), is a legitimate cause of `won` from ANY state that
// isn't already `won`: payment always wins, and "changed their mind later" —
// after cancelling, after declining months ago, after the request went stale —
// is most of sales. `won` stays terminal. Consequence, accepted: the funnel
// counts a recovered lead in BOTH the lost/expired/cancelled tile AND the won
// tile (counting is "ever reached"), same shape as no_show → reschedule → held.

const TRIAL_STATUS_TRANSITIONS = {
  requested: ['scheduled', 'expired', 'won'],
  scheduled: ['held', 'no_show', 'cancelled', 'scheduled', 'won'],
  held:      ['won', 'lost'],
  no_show:   ['won', 'lost', 'scheduled'],
  cancelled: ['scheduled', 'won'],
  won:       [],
  lost:      ['won'],
  expired:   ['won'],
};

export function getAllowedTransitions(status) {
  return TRIAL_STATUS_TRANSITIONS[status] ?? [];
}

export function isLegalTransition(fromStatus, toStatus) {
  return getAllowedTransitions(fromStatus).includes(toStatus);
}

export function requiresOutcomeReason(status) {
  return status === 'lost' || status === 'no_show';
}

// ─── statusHistory ──────────────────────────────────────────────────────────
// Mirrors RenewalManager's appendStatusHistoryFE ({ status, changedAt, source })
// with an added `by` (acting admin's uid) — trial writes are always a named
// human, and the override path needs a real audit trail.
// `source` is a short action tag, e.g. 'schedule' | 'outcome' | 'override' |
// 'reschedule'. `detail` is an optional short string for entries that need
// one — a reschedule records what moved (old → new time/teacher) so "how
// often do we reschedule, and does it hurt conversion" is answerable later.
// Entries written before `detail` existed simply don't have the key; treat a
// missing detail as null — no migration, no backfill.

export function appendTrialStatusHistory(trial, status, by, source, detail = null) {
  const existing = Array.isArray(trial?.statusHistory) ? trial.statusHistory : [];
  return [...existing, { status, changedAt: new Date().toISOString(), source, by, detail }];
}

// ─── Timezone derivation ────────────────────────────────────────────────────
// Trials don't store a timezone field — it's derived from intake.country/
// stateProvince (the same vocabulary ProfileContactFields writes) via the
// existing resolveTimezone lookup. Returns null when it can't resolve
// (e.g. a country outside STATE_TIMEZONES/COUNTRY_TIMEZONES), and callers
// should show the scheduled time with no zone label rather than guess.

export function getTrialTimezone(trial) {
  return resolveTimezone(trial?.intake?.country, trial?.intake?.stateProvince);
}

// ─── Funnel math ────────────────────────────────────────────────────────────

function isInDateRange(dateStr, start, end) {
  // Matches AdminDashboard's isInRange: missing bounds or missing date passes through
  // rather than being excluded, so records with no date aren't silently hidden.
  if (!dateStr || !start || !end) return true;
  try {
    const d = new Date(dateStr);
    return d >= start && d <= end;
  } catch {
    return false;
  }
}

/**
 * Filters trial docs by date range (on createdAt), teacherId, and referral code.
 * Exported standalone so TrialManager can render its list from the exact same
 * filtered array the stat tiles are computed from — numbers and rows stay in sync.
 */
export function filterTrials(trials, filters = {}) {
  const { startDate = null, endDate = null, teacherId = null, code = null } = filters;
  return (trials ?? []).filter(t => {
    if (!isInDateRange(t.createdAt, startDate, endDate)) return false;
    if (teacherId && t.teacherId !== teacherId) return false;
    if (code && t.attribution?.code !== code) return false;
    return true;
  });
}

// Statuses a trial doc ever passed through, from statusHistory. Falls back to
// the live status when statusHistory is missing/empty so a malformed or very
// old doc still counts toward something instead of silently vanishing.
// Intended consequence of "ever reached": a trial that no-showed, got
// rescheduled, then was held counts in BOTH the no_show and held tiles. Both
// are true and both are useful — do not "fix" this to count only the latest
// occurrence of each status.
function getEverReachedStatuses(trial) {
  const history = Array.isArray(trial?.statusHistory) ? trial.statusHistory : [];
  if (history.length === 0) {
    return trial?.status ? new Set([trial.status]) : new Set();
  }
  return new Set(history.map(h => h?.status).filter(Boolean));
}

/**
 * Pure funnel stats over an array of trial docs, filtered first by
 * filterTrials. Siblings from a multi-student submission (same groupId) are
 * each their own trial doc and are counted independently here — conversion is
 * measured per student, not per family, so that's intended, not a bug.
 * NOTE the deliberate split in what "counts" means here vs.
 * elsewhere: these tiles count "ever reached this status" (from
 * statusHistory), answering a funnel/drop-off question — e.g. counts.requested
 * equals every trial, since all of them start there, even ones now sitting at
 * 'won'. TrialManager's section lists (Needs Scheduling / Upcoming / etc.) use
 * the live `status` field instead, answering "what do I do right now" — the
 * two are intentionally different lenses on the same data.
 *   - noShowRate: of trials that ever reached held or no_show, the fraction
 *     that ever reached no_show.
 *   - heldToWonRate / requestedToWonRate / wonWithoutHeld: see the comment
 *     block above their computation below — Stage 5 added a second rate
 *     deliberately, they disagree on purpose, and neither is wrong.
 * `filtered` is included in the result so callers don't need to call
 * filterTrials a second time to render the matching list (by live status).
 */
export function computeTrialFunnelStats(trials, filters = {}) {
  const filtered = filterTrials(trials, filters);

  const counts = TRIAL_STATUSES.reduce((acc, s) => {
    acc[s] = 0;
    return acc;
  }, {});

  // Trials that reached `won` without ever reaching `held` — a direct
  // conversion from requested / scheduled / no_show / cancelled / lost /
  // expired (payment always wins, Phase 2 decision D1). Used below.
  let wonWithoutHeld = 0;

  filtered.forEach(t => {
    const reached = getEverReachedStatuses(t);
    reached.forEach(s => {
      if (Object.prototype.hasOwnProperty.call(counts, s)) {
        counts[s]++;
      }
    });
    if (reached.has('won') && !reached.has('held')) wonWithoutHeld++;
  });

  const outcomeResolved = counts.held + counts.no_show;
  const noShowRate = outcomeResolved > 0 ? counts.no_show / outcomeResolved : null;

  // ── Two conversion rates, kept deliberately separate (Stage 5 audit) ──────
  //
  // heldToWonRate: of trials that ever reached `held`, the fraction that ever
  // reached `won`. Definition and label UNCHANGED since Phase 0 — this answers
  // "once we get someone into a session, do we close them?" and stays useful
  // for judging the trial SESSIONS themselves, independent of everything else.
  //
  // Since Phase 2, payment always wins (D1): a trial can convert directly from
  // requested / scheduled / no_show / cancelled / lost / expired, landing on
  // `won` with no `held` in its history. heldToWonRate's numerator
  // (counts.won) includes those conversions; its denominator (counts.held)
  // does not — so this rate CAN read over 100%. That is a true fact about the
  // funnel, not a bug to clamp away: it means conversions are increasingly
  // happening outside the trial-session pipeline, which is precisely the kind
  // of thing this metric exists to surface. wonWithoutHeld is the count behind
  // that gap, so the "why" is never just an unexplained percentage.
  //
  // requestedToWonRate: of every trial requested, the fraction that ever
  // reached `won` — counts.won / counts.requested, NOT counts.won /
  // filtered.length, even though today they are the same number. Confirmed
  // (Stage 5 recon, Q1): a trial's LIVE status can start at 'scheduled' (an
  // admin filling in a teacher/time at creation), but its statusHistory always
  // writes 'requested' FIRST regardless — both creation sites do this
  // unconditionally:
  //   - TrialManager.js (~line 1717): appendTrialStatusHistory(..., 'requested',
  //     ..., 'create') is called before the 'scheduled'/'create_scheduled'
  //     entry is ever appended, even when the trial goes live already scheduled.
  //   - createTrialSignup (functions/index.js): every self-signup trial is
  //     created with status 'requested' and a single matching statusHistory
  //     entry; scheduling only ever happens as a later, separate write.
  // So counts.requested equals `filtered.length` for any doc created by either
  // path — this rate is IMMUNE to the heldToWonRate skip problem, because
  // every path to `won`, whatever it skips in between, passes through
  // 'requested'. This is "of everyone who asked for a trial, how many became a
  // customer" — the number the business is actually run on.
  //
  // Caveat this shares with every "ever reached" count here (see
  // getEverReachedStatuses above): a doc with empty/missing statusHistory falls
  // back to its live `status` alone, so a malformed or manually-edited
  // Firestore doc — history wiped, status left at something other than
  // 'requested' — would count toward `won` (or whatever its status is) without
  // counting toward `requested`, inflating this rate. That's a pre-existing
  // property of the fallback, not new here, and is expected to be rare.
  //
  // Forward-looking requirement: counts.requested is used as this rate's
  // denominator SPECIFICALLY because both current creation paths guarantee a
  // 'requested' statusHistory entry. Any future trial creation path MUST also
  // write 'requested' into statusHistory first — this invariant is
  // load-bearing for requestedToWonRate, not incidental.
  //
  // Neither rate is wrong; they answer different questions and are expected to
  // disagree once conversions start skipping `held`.
  const heldToWonRate = counts.held > 0 ? counts.won / counts.held : null;
  const requestedToWonRate = counts.requested > 0 ? counts.won / counts.requested : null;

  return {
    total: filtered.length,
    counts,
    noShowRate,
    heldToWonRate,
    requestedToWonRate,
    wonWithoutHeld,
    filtered,
  };
}
